import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LogIn, Shield, FileText, TrendingUp, BarChart3, Mail, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Login = () => {
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });

  const features = [
    { 
      icon: TrendingUp, 
      title: 'Suivi de l\'activite', 
      subtitle: 'Pilotage des ventes',
      description: 'Consultez en temps reel l\'etat d\'avancement de vos dossiers, le statut de vos ventes et les indicateurs cles lies a votre activite.' 
    },
    { 
      icon: FileText, 
      title: 'Ressources operationnelles', 
      subtitle: 'Documentation centralisee',
      description: 'Accedez a l\'ensemble des documents officiels et supports necessaires a vos missions : procedures, grilles tarifaires, outils commerciaux, ressources marketing et documents administratifs.' 
    },
    { 
      icon: BarChart3, 
      title: 'Performance commerciale', 
      subtitle: 'Indicateurs et positionnement',
      description: 'Visualisez vos resultats, suivez votre niveau d\'activite et consultez les indicateurs de performance ainsi que les classements periodiques.' 
    },
  ];

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    if (!loginForm.email || !loginForm.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${API}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(loginForm),
      });
      
      if (response.ok) {
        toast.success('Connexion reussie !');
        window.location.href = '/dashboard';
      } else if (response.status === 401) {
        toast.error('Email ou mot de passe incorrect');
      } else if (response.status === 520) {
        toast.error('Erreur serveur temporaire, reessayez');
      } else {
        toast.error('Erreur de connexion');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Hero */}
      <div 
        className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-900"
      >
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="flex items-center gap-4 mb-8">
            <img 
              src="/logo-groupe-lnc.png" 
              alt="GROUPE-LNC Logo" 
              className="w-14 h-14 object-contain bg-white/10 rounded-2xl p-2 backdrop-blur-xl border border-white/20"
            />
            <div>
              <h1 className="text-3xl font-bold text-white tracking-tight font-[Manrope]">GROUPE-LNC</h1>
              <p className="text-indigo-200 text-sm">Portail Commercial</p>
            </div>
          </div>
          
          <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-6 font-[Manrope]">
            Environnement de gestion de votre activite
          </h2>
          <p className="text-lg text-indigo-200 mb-12 max-w-xl leading-relaxed">
            Le Portail Commercial GROUPE-LNC constitue votre interface de travail dediee. Il centralise l'ensemble des informations, outils et ressources necessaires au pilotage de votre activite et au suivi de vos operations commerciales.
          </p>

          <div className="grid gap-5">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="p-5 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 animate-fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-white" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{feature.title}</h3>
                    <p className="text-sm text-indigo-300">{feature.subtitle}</p>
                  </div>
                </div>
                <p className="text-sm text-indigo-200 leading-relaxed pl-15">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right side - Login */}
      <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-8 bg-slate-50 dark:bg-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <img 
              src="/logo-groupe-lnc.png" 
              alt="GROUPE-LNC Logo" 
              className="w-12 h-12 object-contain"
            />
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white font-[Manrope]">GROUPE-LNC</h1>
              <p className="text-sm text-slate-500">Portail Commercial</p>
            </div>
          </div>

          <Card className="border-0 shadow-xl shadow-slate-200/50 dark:shadow-none">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold font-[Manrope]">Connexion</CardTitle>
              <CardDescription className="text-base">
                Entrez vos identifiants pour acceder a votre espace
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="votre@email.com"
                      className="pl-10"
                      value={loginForm.email}
                      onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="login-password"
                      type="password"
                      placeholder="********"
                      className="pl-10"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full h-12 bg-indigo-600 hover:bg-indigo-700"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <LogIn className="w-4 h-4 mr-2" />
                  )}
                  Se connecter
                </Button>
              </form>

              <div className="flex items-center gap-3 p-4 rounded-xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-100 dark:border-indigo-900">
                <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Contactez votre administrateur pour obtenir vos identifiants
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
